import { useState, type CSSProperties } from "react";
import { Copy, Check } from "lucide-react";
import JsonView from "@uiw/react-json-view";
import { githubDarkTheme } from "@uiw/react-json-view/githubDark";
import { cn } from "@/lib/utils";

interface JsonViewerProps {
  value: unknown;
  label?: string;
  className?: string;
  /** Whether the panel starts collapsed inside a <details> wrapper */
  collapsible?: boolean;
  defaultOpen?: boolean;
}

function safeStringify(value: unknown): string {
  try {
    return JSON.stringify(value, null, 2);
  } catch {
    try {
      return String(value);
    } catch {
      return "[unserializable value]";
    }
  }
}

function isRenderableObject(value: unknown): value is object {
  return typeof value === "object" && value !== null;
}

// Override the theme's background so our outer container controls it; keep
// the syntax-highlighting colors from githubDark.
const viewerStyle = {
  ...githubDarkTheme,
  backgroundColor: "transparent",
} as CSSProperties;

export function JsonViewer({
  value,
  label,
  className,
  collapsible = false,
  defaultOpen = true,
}: JsonViewerProps) {
  const [copied, setCopied] = useState(false);

  function handleCopy() {
    void navigator.clipboard.writeText(safeStringify(value)).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  }

  const viewerBody = isRenderableObject(value) ? (
    <JsonView
      value={value}
      style={viewerStyle}
      collapsed={2}
      displayDataTypes={false}
      enableClipboard={false}
      shortenTextAfterLength={120}
    />
  ) : (
    <pre className="font-mono text-xs leading-relaxed whitespace-pre-wrap text-slate-100">
      {safeStringify(value)}
    </pre>
  );

  const content = (
    <div className={cn("relative", className)}>
      <button
        type="button"
        onClick={handleCopy}
        aria-label="Copy JSON"
        className="absolute top-2 right-2 z-10 flex items-center gap-1 rounded bg-slate-800/80 px-2 py-1 text-xs text-slate-300 hover:bg-slate-700 hover:text-slate-100"
      >
        {copied ? (
          <Check className="h-3 w-3 text-green-400" />
        ) : (
          <Copy className="h-3 w-3" />
        )}
        {copied ? "Copied" : "Copy"}
      </button>
      <div
        data-testid="json-viewer-body"
        className="max-h-[60vh] overflow-auto rounded-lg bg-slate-950 p-4 text-xs"
      >
        {viewerBody}
      </div>
    </div>
  );

  if (!collapsible) {
    return (
      <div>
        {label && (
          <p className="text-foreground mb-1 text-sm font-medium">{label}</p>
        )}
        {content}
      </div>
    );
  }

  return (
    <details open={defaultOpen} className="group">
      <summary className="text-foreground mb-1 cursor-pointer text-sm font-medium select-none">
        {label ?? "JSON"}
      </summary>
      {content}
    </details>
  );
}
